<template>
  <pl-dialog
    title="Import Feedback Templates"
    size="md"
    :disableOutsideClick="true"
    @close="closeCurrentModal()"
  >
    <header class="px-6">
      <!-- Information -->
      <div class="flex space-x-3 items-center mb-4 mt-3">
        <span>
          This will import all of the saved feedback templates from the course
          you select. Any edits you make will not be reflected in the old
          course, so you can change or delete any imported templates.
        </span>
      </div>
      <!-- Search -->
      <div class="flex w-full space-x-2 mb-4">
        <div class="w-full">
          <div class="text-gray-400 relative">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3.5">
              <button
                type="submit"
                class="focus:outline-none focus:shadow-outline"
              >
                <i class="fa-solid fa-magnifying-glass"></i>
              </button>
            </span>
            <input
              autocomplete="off"
              class="w-full py-2 pl-9 px-4 flex justify-between border-coolGray-300 items-center border-b border-l border-r rounded"
              placeholder="Search course..."
              ref="searchInput"
              v-model="search"
            />
          </div>
        </div>
      </div>
    </header>
    <body
      class="w-full flex-grow overflow-y-auto h-80 mb-4 center items-center px-6 relative"
    >
      <div>
        <ul>
          <!-- List Courses -->
          <li
            v-for="(course, index) in coursesFilterByName"
            @click="handleSelectCourse(course)"
            :key="index"
            :class="isCourseSelected(course.id)"
          >
            <div>
              <label :for="course.title">
                {{ course.title | truncate(26) }}
              </label>
            </div>
            <div>
              <input
                type="checkbox"
                v-model="selectedCourses"
                :value="course"
              />
            </div>
          </li>
        </ul>
        <!-- No Results.... -->
        <div v-if="coursesFilterByName.length === 0 && isLoading === false">
          <span>No Results Found...</span>
        </div>
      </div>
    </body>
    <footer class="px-6 mb-5">
      <div class="flex justify-between items-center">
        <button
          @click="closeCurrentModal()"
          class="bg-gray-100 border hover:border-gray-300 border-solid border-gray-200 text-sm text-gray-600 rounded px-6 py-2"
        >
          Cancel
        </button>
        <button
          @click="importFeedbackTemplates()"
          :class="selectedCoursesTotal"
        >
          <div class="flex justify-center space-x-2">
            <span>{{
              isImporting === true
                ? 'Importing'
                : `Import (${selectedCourses.length})`
            }}</span>
            <div class="loader" v-show="isImporting"></div>
          </div>
        </button>
      </div>
    </footer>
  </pl-dialog>
</template>
<script>
  import Vue from 'vue';
  import { EventBus } from 'utils/event-bus';
  import { mapGetters } from 'vuex';

  /* Services */
  import Course_Service from 'services/courses';

  export default Vue.extend({
    props: {
      typeRoundtable: { type: String, default: 'online' },
    },
    data: () => ({
      coursesWithFeedback: [],
      isLoading: false,
      selectedCourses: [],
      selected_index: null,
      isImporting: false,
      search: '',
    }),
    computed: {
      ...mapGetters(['getCourses', 'getCourseDetail']),
      coursesFilterByName: function () {
        if (!this.search) return this.coursesWithFeedback;
        const filter_course = this.coursesWithFeedback.filter((course) => {
          return (
            course.title.toLowerCase().indexOf(this.search.toLowerCase()) != -1
          );
        });
        return filter_course;
      },
      selectedCoursesTotal: function () {
        const default_class = 'text-sm text-white rounded px-6 py-2';
        if (this.selectedCourses.length === 0) {
          return default_class + ' bg-primary bg-opacity-50';
        }
        return default_class + ' bg-primary';
      },
    },
    methods: {
      closeCurrentModal: async function () {
        return this.$emit('close');
      },
      handleSelectCourse: function (clickedCourse) {
        const is_course_exist = this.selectedCourses.find(
          (item) => item.id === clickedCourse.id
        );
        if (is_course_exist === undefined) {
          return this.selectedCourses.push(clickedCourse);
        }
        const index = this.selectedCourses.findIndex(
          (course) => course.id === clickedCourse.id
        );
        this.selectedCourses.splice(index, 1);
      },
      fetchAllCourses: async function () {
        /* Accepted Types: ( Online, Live ) */
        this.isLoading = true;
        const courses = await Course_Service.showCoursesWithFeedback(
          this.getCourseDetail.id,
          this.typeRoundtable
        );
        if (!courses) {
          const toast = {
            type: 'Error',
            message: 'Something happened getting courses...',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          this.isLoading = false;
          return;
        }
        this.isLoading = false;
        return courses;
      },
      importFeedbackTemplates: async function () {
        if (this.selectedCourses.length === 0) {
          const toast = {
            type: 'Error',
            message: 'You need select at least one course...',
            time: 3000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }
        const courses_ids = this.selectedCourses.map((course) => course.id);
        this.isImporting = true;
        const typeRoundtable =
          this.$route.params.liveDiscussionID ||
          this.$route.query.type === 'live_discussion'
            ? 'Live'
            : 'Online';
        await this.$store.dispatch(`importFeedback${typeRoundtable}`, {
          course_id: this.getCourseDetail.id,
          courses: courses_ids,
        });
        const toast = {
          type: 'Success',
          message: 'Feedback templates are imported...',
          time: 5000,
        };
        EventBus.$emit('createToast', toast);
        this.isImporting = false;
        this.closeCurrentModal();
        return;
      },
      isCourseSelected: function (course_id) {
        const default_class =
          'flex cursor-pointer bg-gray-50 py-2 mb-2 border border-solid justify-between items-center rounded px-4 w-full';
        const is_course_exist = this.selectedCourses.find(
          (item) => item.id === course_id
        );
        if (is_course_exist === undefined) {
          return default_class;
        }
        return (
          default_class + ' border border-solid border-primary bg-coolGray-100'
        );
      },
    },
    mounted: async function () {
      const courses = await this.fetchAllCourses();
      this.coursesWithFeedback = courses.data.filter((course) => {
        if (course.id !== this.$store.state.course_sample_id) {
          return course;
        }
      });
    },
  });
</script>
<style scoped>
  input[type='checkbox'] {
    margin-top: 4px;
    width: 1.25rem !important;
    height: 1.25rem !important;
    -webkit-appearance: none;
    -moz-appearance: none;
    -o-appearance: none;
    appearance: none;
    box-shadow: none;
    border-color: #d7d7d7;
    font-size: 0.8rem;
    text-align: center;
    line-height: 1.25rem;
    border-radius: 0.25rem;
    background-color: rgba(243, 244, 246, var(--tw-bg-opacity));
  }
  input[type='checkbox']:checked {
    background-color: #72ccd7;
    margin-bottom: 5px;
    margin-top: 4px;
    border-color: #72ccd7;
  }
  input[type='checkbox']:checked::after {
    font-family: 'Font Awesome 5 Free';
    content: '\f00c';
    font-weight: 900;
    color: white;
    line-height: 1.25rem;
  }
  .loader,
  .loader:after {
    border-radius: 50%;
    width: 20px;
    height: 20px;
    margin-left: 12px;
    margin-top: 1px;
  }
  .loader {
    border-top: 3px solid rgba(255, 255, 255, 0.2);
    border-right: 3px solid rgba(255, 255, 255, 0.2);
    border-bottom: 3px solid rgba(255, 255, 255, 0.2);
    border-left: 3px solid #ffffff;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 1.1s infinite linear;
  }
  @-webkit-keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  @keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
</style>



// WEBPACK FOOTER //
// ImportFeedbackModal.vue?4aebb016